import { useQuery } from '@tanstack/react-query';
import { cotizacionesApi } from '@/services/cotizacionesApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { TIPO_OPERACION_LABELS, ESTADO_COTIZACION_LABELS, ESTADO_COLORS } from '@/utils/constants';
import { DollarSign, Users } from 'lucide-react';

export function Dashboard() {
  const { data } = useQuery({
    queryKey: ['cotizaciones-dashboard'],
    queryFn: () => cotizacionesApi.findAll(0, 5),
  });

  const { data: stats } = useQuery({
    queryKey: ['cotizaciones-stats'],
    queryFn: cotizacionesApi.getStats,
  });

  const totalValor = stats?.valorAceptadas || 0;
  const cotizacionesHoy = stats?.cotizacionesHoy || 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
              <DollarSign className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Valor Aceptado</p>
              <p className="text-2xl font-bold">{formatCurrency(totalValor)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center shrink-0">
              <Users className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Cotizaciones Hoy</p>
              <p className="text-2xl font-bold">{cotizacionesHoy}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Quotations */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Últimas 5 Cotizaciones</h3>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cotizador</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody>
              {data?.content.map((cot) => (
                <tr key={cot.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{cot.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(cot.fechaCreacion)}</td>
                  <td className="px-4 py-3 text-sm">{cot.cliente.nombre}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{cot.usuarioNombre}</td>
                  <td className="px-4 py-3 text-sm">{TIPO_OPERACION_LABELS[cot.tipoOperacion]}</td>
                  <td className="px-4 py-3 text-sm font-medium">{formatCurrency(cot.totalFinal)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORS[cot.estadoCotizacion]}`}>
                      {ESTADO_COTIZACION_LABELS[cot.estadoCotizacion]}
                    </span>
                  </td>
                </tr>
              ))}
              {(!data || data.content.length === 0) && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">No hay cotizaciones aún</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-gray-100">
          {data?.content.map((cot) => (
            <div key={cot.id} className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">#{cot.id}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORS[cot.estadoCotizacion]}`}>
                  {ESTADO_COTIZACION_LABELS[cot.estadoCotizacion]}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900">{cot.cliente.nombre}</p>
              <p className="text-xs text-gray-500 mt-0.5">{cot.usuarioNombre}</p>
              <div className="flex items-center justify-between mt-2 text-sm">
                <span className="text-gray-500">{formatDate(cot.fechaCreacion)}</span>
                <span className="font-bold">{formatCurrency(cot.totalFinal)}</span>
              </div>
            </div>
          ))}
          {(!data || data.content.length === 0) && (
            <p className="text-center py-8 text-gray-500">No hay cotizaciones aún</p>
          )}
        </div>
      </div>
    </div>
  );
}
